// Minimal line-level diff (LCS) used to render a GitHub-style visual diff of a
// prompt body against its previous saved version. No diff dependency exists in
// the project, and `deep-diff` only handles object graphs, not text.

export type DiffLineType = `context` | `add` | `remove`

export type DiffLine = {
  type: DiffLineType
  text: string
  /** 1-based line number in the old body, or null for added lines. */
  oldNumber: number | null
  /** 1-based line number in the new body, or null for removed lines. */
  newNumber: number | null
}

/**
 * Computes a line-by-line diff between `oldText` and `newText` using a longest
 * common subsequence table, yielding rows in display order (context/add/remove).
 */
export function lineDiff(oldText: string, newText: string): DiffLine[] {
  const oldLines = oldText.split(`\n`)
  const newLines = newText.split(`\n`)
  const n = oldLines.length
  const m = newLines.length

  // dp[i][j] = length of the LCS of oldLines[i..] and newLines[j..].
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = oldLines[i] === newLines[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
    }
  }

  const result: DiffLine[] = []
  let i = 0
  let j = 0
  let oldNo = 1
  let newNo = 1
  while (i < n && j < m) {
    if (oldLines[i] === newLines[j]) {
      result.push({ type: `context`, text: oldLines[i], oldNumber: oldNo++, newNumber: newNo++ })
      i++
      j++
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      result.push({ type: `remove`, text: oldLines[i], oldNumber: oldNo++, newNumber: null })
      i++
    } else {
      result.push({ type: `add`, text: newLines[j], oldNumber: null, newNumber: newNo++ })
      j++
    }
  }
  while (i < n) {
    result.push({ type: `remove`, text: oldLines[i], oldNumber: oldNo++, newNumber: null })
    i++
  }
  while (j < m) {
    result.push({ type: `add`, text: newLines[j], oldNumber: null, newNumber: newNo++ })
    j++
  }
  return result
}
